import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../../components/ui/badge';
import { TrendingUp, Users, BookOpen, Activity, Award, BarChart3, Building, PieChart } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminApi, schoolsApi } from '../../lib/api';
import { toast } from 'sonner';

export const GrowthAnalytics = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    fetchData();
    if (user?.role === 'super_admin') {
      schoolsApi.getAll().then(setSchools).catch(console.error);
    }
  }, [user]);

  const fetchData = async (schoolId = null) => {
    setLoading(true);
    try {
      const result = await adminApi.getOrganizationGrowth(schoolId);
      setData(result);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolChange = (schoolId) => {
    setSelectedSchool(schoolId);
    fetchData(schoolId || null);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-48" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-32 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="growth-analytics">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <TrendingUp className="w-6 h-6" />
          Organization Growth
        </h1>
        {user?.role === 'super_admin' && (
          <Select value={selectedSchool || "all"} onValueChange={(v) => handleSchoolChange(v === "all" ? "" : v)}>
            <SelectTrigger className="w-64" data-testid="school-filter">
              <SelectValue placeholder="All Schools" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Schools</SelectItem>
              {schools.map(school => (
                <SelectItem key={school.school_id} value={school.school_id}>{school.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Overview Stats */}
      {data?.overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {data.overview.total_schools !== undefined && (
            <div className="stat-card bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20">
              <Building className="w-8 h-8 text-blue-500 mb-2" />
              <div className="text-3xl font-bold">{data.overview.total_schools}</div>
              <div className="text-sm text-muted-foreground">Total Schools</div>
            </div>
          )}
          <div className="stat-card bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20">
            <Users className="w-8 h-8 text-purple-500 mb-2" />
            <div className="text-3xl font-bold">{data.overview.total_users || data.overview.total_students || 0}</div>
            <div className="text-sm text-muted-foreground">{data.overview.total_users ? 'Total Users' : 'Students'}</div>
          </div>
          <div className="stat-card bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20">
            <BookOpen className="w-8 h-8 text-green-500 mb-2" />
            <div className="text-3xl font-bold">{data.overview.total_enrollments || 0}</div>
            <div className="text-sm text-muted-foreground">Enrollments</div>
          </div>
          <div className="stat-card bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20">
            <Award className="w-8 h-8 text-amber-500 mb-2" />
            <div className="text-3xl font-bold">{data.overview.completed_courses || 0}</div>
            <div className="text-sm text-muted-foreground">Completions</div>
          </div>
        </div>
      )}

      {/* School-specific stats */}
      {data?.school && (
        <div className="p-4 rounded-lg bg-card border border-border">
          <h3 className="font-semibold mb-2">{data.school.name}</h3>
          <p className="text-sm text-muted-foreground">{data.school.address || 'No address'}</p>
        </div>
      )}

      {/* User Breakdown */}
      {data?.user_breakdown && (
        <div className="rounded-lg bg-card border border-border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5" />
            User Distribution
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(data.user_breakdown).map(([role, count]) => (
              <div key={role} className="text-center p-3 rounded-lg bg-muted/50">
                <div className="text-2xl font-bold">{count}</div>
                <div className="text-xs text-muted-foreground capitalize">{role.replace('_', ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-School Breakdown */}
      {data?.schools && data.schools.length > 0 && (
        <div className="rounded-lg bg-card border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              School Performance
            </h3>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>School</th>
                <th>Users</th>
                <th>Students</th>
                <th>Faculty</th>
                <th>Enrollments</th>
                <th>Completed</th>
              </tr>
            </thead>
            <tbody>
              {data.schools.map(school => (
                <tr key={school.school_id}>
                  <td className="font-medium">{school.name}</td>
                  <td>{school.total_users}</td>
                  <td>{school.students}</td>
                  <td>{school.faculty}</td>
                  <td>{school.enrollments}</td>
                  <td>
                    <Badge variant="outline" className="bg-green-500/10 text-green-600">
                      {school.completed}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top Courses */}
      {data?.top_courses && data.top_courses.length > 0 && (
        <div className="rounded-lg bg-card border border-border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Popular Courses
          </h3>
          <div className="space-y-3">
            {data.top_courses.map((course, idx) => (
              <div key={course.course_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold">
                    {idx + 1}
                  </span>
                  <div>
                    <span className="font-medium">{course.title}</span>
                    <Badge variant="outline" className="ml-2">{course.language || course.category?.replace('_', ' ') || 'Course'}</Badge>
                  </div>
                </div>
                <span className="text-muted-foreground">{course.enrollments} enrollments</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Exam Dashboard Component
